import { useCallback, useEffect, useRef, useState } from "react";
import { http, formatApiError } from "../../lib/api";
import { usePtiMonitor, useWakeLock, useAlarmSound } from "../../lib/usePtiMonitor";

export const PTI_LABELS = { sos: "SOS", fall: "Chute / perte de verticalité", no_motion: "Absence de mouvement", checkin: "Contrôle « tout va bien » manqué" };

// État global du PTI dans l'app agent (reste actif quel que soit l'onglet)
export function usePtiController() {
  const [cfg, setCfg] = useState(null);
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState({ kind: "idle" });
  const [now, setNow] = useState(Date.now());
  const [notice, setNotice] = useState("");
  const pos = useRef({});

  useEffect(() => { http.get("/agent-app/pti/config").then((r) => setCfg(r.data)).catch(() => setCfg({ available: false })); }, []);

  useEffect(() => {
    if (!active || !navigator.geolocation) return undefined;
    const id = navigator.geolocation.watchPosition((p) => { pos.current = { lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy }; },
      () => {}, { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [active]);

  const send = useCallback(async (type) => {
    setPhase({ kind: "sending", type });
    for (;;) {
      try {
        const r = await http.post("/agent-app/pti/alarm", { type, ...pos.current });
        setPhase({ kind: "sent", type, alarm: r.data });
        return;
      } catch (e) {
        if (e?.response?.status === 400 || e?.response?.status === 403) { setPhase({ kind: "idle" }); setNotice(formatApiError(e)); return; }
        await new Promise((res) => setTimeout(res, 5000));
      }
    }
  }, []);

  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const trigger = useCallback((type) => {
    if (phaseRef.current.kind !== "idle") return;
    if (type === "sos") { phaseRef.current = { kind: "sending" }; send("sos"); return; }
    setPhase({ kind: "prealarm", type, endsAt: Date.now() + (cfg?.prealarm_seconds || 15) * 1000 });
  }, [cfg, send]);

  const mon = usePtiMonitor(cfg, active, phase.kind !== "idle", trigger);
  useWakeLock(active);
  useAlarmSound(phase.kind === "prealarm");

  useEffect(() => {
    if (phase.kind !== "prealarm") return undefined;
    const iv = setInterval(() => { setNow(Date.now()); if (Date.now() >= phase.endsAt) { clearInterval(iv); send(phase.type); } }, 250);
    return () => clearInterval(iv);
  }, [phase, send]);

  useEffect(() => {
    if (phase.kind !== "sent" || phase.alarm.transmission?.ok !== null) return undefined;
    const iv = setInterval(async () => {
      const r = await http.get("/agent-app/pti/alarms").catch(() => null);
      const a = r?.data.find((x) => x.id === phase.alarm.id);
      if (a && a.transmission?.ok !== null) setPhase((p) => (p.kind === "sent" && p.alarm.id === a.id ? { ...p, alarm: a } : p));
    }, 3000);
    return () => clearInterval(iv);
  }, [phase]);

  const start = async () => {
    setNotice("");
    try {
      if (typeof DeviceMotionEvent !== "undefined" && typeof DeviceMotionEvent.requestPermission === "function") {
        if ((await DeviceMotionEvent.requestPermission()) !== "granted") { setNotice("Autorisez l'accès aux capteurs de mouvement pour activer le PTI."); return; }
      }
    } catch { setNotice("Autorisez l'accès aux capteurs de mouvement pour activer le PTI."); return; }
    setActive(true);
    http.post("/agent-app/pti/session", { action: "start" }).catch(() => {});
  };
  const stop = () => {
    if (!window.confirm("Désactiver la protection PTI ?")) return;
    setActive(false); setPhase({ kind: "idle" });
    http.post("/agent-app/pti/session", { action: "stop" }).catch(() => {});
  };
  const imOk = () => { setPhase({ kind: "idle" }); mon.resetCheckin(); };
  const declare = async (reason) => {
    try {
      await http.post(`/agent-app/pti/alarm/${phase.alarm.id}/cancel`, { reason });
      setNotice(reason === "test" ? "Alarme déclarée comme test : la télésurveillance est prévenue." : "Erreur de manipulation signalée à la télésurveillance.");
      setPhase({ kind: "idle" }); mon.resetCheckin();
    } catch (e) { setNotice(formatApiError(e)); }
  };
  const close = () => { setPhase({ kind: "idle" }); mon.resetCheckin(); };
  const left = phase.kind === "prealarm" ? Math.max(0, Math.ceil((phase.endsAt - Math.max(now, Date.now())) / 1000)) : 0;

  return { cfg, active, phase, left, notice, start, stop, trigger, imOk, declare, close, ...mon };
}
